/**
 * Save System — localStorage-backed save/load with versioning and migrations.
 *
 * Design notes:
 * - Keys live under `surggies:save:<slot>`.
 * - All public functions are SSR-safe: if `window` is undefined, they no-op
 *   (return null / false) rather than throw.
 * - All localStorage interactions are wrapped in try/catch — quota exceeded,
 *   JSON parse errors, security exceptions in private-mode browsers, etc.
 *   must never bubble out.
 * - Validation is hand-rolled (no zod) via duck-typed guards. Anything that
 *   fails validation returns null.
 * - Migration is structured so future SAVE_VERSION bumps drop new step
 *   functions into the `MIGRATIONS` table without touching call sites.
 */

import type { PluginLoadout } from "./plugin-system";

export type SaveSlotId = "auto" | "manual1" | "manual2" | "manual3";

export const SAVE_VERSION = 2;

export type Facing = "down" | "left" | "right" | "up";

export type SaveData = {
  version: number;
  savedAt: number;
  playTimeMs: number;
  player: {
    x: number;
    y: number;
    facing: Facing;
    mapId: string;
    name: string;
    // v2+: soft currency. Defaults to 0 for migrated v1 saves.
    coins?: number;
  };
  party: Array<{
    speciesId: string;
    nickname?: string;
    level: number;
    xp: number;
    hpCurrent: number;
    hpMax: number;
    moves: string[];
    pp: Record<string, number>;
    // v2+: installed plugin loadout. Optional so callers that don't yet
    // populate it (and v1 saves migrated to v2) continue to type-check.
    loadout?: PluginLoadout;
  }>;
  pcBox: Array<{ speciesId: string; level: number; xp: number }>;
  // Items inventory — flat `itemId → count` map. Shape preserved from v1
  // so existing callers (e.g. GameSystemsProvider's `setInventory`) keep
  // working without modification.
  inventory: Record<string, number>;
  // v2+: owned plugins, keyed by pluginId → count owned. Kept as a sibling
  // of `inventory` instead of nested under it to preserve the flat
  // `Record<string, number>` shape of `inventory` for legacy callers.
  // Conceptually "inventory.plugins" — physically a top-level sibling.
  // Optional in the type so callers building v1-style SaveData literals
  // continue to type-check; the load path normalizes missing → `{}`.
  pluginInventory?: Record<string, number>;
  progress: {
    defeatedTrainers: string[];
    flags: Record<string, boolean>;
    seenSpecies: string[];
    caughtSpecies: string[];
  };
};

const STORAGE_PREFIX = "surggies:save:";

const VALID_SLOTS: ReadonlyArray<SaveSlotId> = [
  "auto",
  "manual1",
  "manual2",
  "manual3",
];

const VALID_FACINGS: ReadonlyArray<Facing> = ["down", "left", "right", "up"];

// ---------- environment ----------

function hasStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function keyFor(slot: SaveSlotId): string {
  return `${STORAGE_PREFIX}${slot}`;
}

function isValidSlot(slot: string): slot is SaveSlotId {
  return (VALID_SLOTS as ReadonlyArray<string>).includes(slot);
}

// ---------- duck-typed guards ----------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}

function isNumberRecord(value: unknown): value is Record<string, number> {
  if (!isRecord(value)) return false;
  for (const key of Object.keys(value)) {
    if (typeof value[key] !== "number" || !Number.isFinite(value[key])) {
      return false;
    }
  }
  return true;
}

function isBooleanRecord(value: unknown): value is Record<string, boolean> {
  if (!isRecord(value)) return false;
  for (const key of Object.keys(value)) {
    if (typeof value[key] !== "boolean") return false;
  }
  return true;
}

function isFacing(value: unknown): value is Facing {
  return typeof value === "string" && (VALID_FACINGS as ReadonlyArray<string>).includes(value);
}

function isPlayer(value: unknown): value is SaveData["player"] {
  if (!isRecord(value)) return false;
  if (
    typeof value.x !== "number" ||
    !Number.isFinite(value.x) ||
    typeof value.y !== "number" ||
    !Number.isFinite(value.y) ||
    !isFacing(value.facing) ||
    typeof value.mapId !== "string" ||
    typeof value.name !== "string"
  ) {
    return false;
  }
  // Optional `coins` — when present must be a finite number.
  if (
    value.coins !== undefined &&
    (typeof value.coins !== "number" || !Number.isFinite(value.coins))
  ) {
    return false;
  }
  return true;
}

function isPluginLoadout(value: unknown): value is PluginLoadout {
  if (!isRecord(value)) return false;
  if (typeof value.speciesId !== "string") return false;
  if (!isStringArray(value.installedPluginIds)) return false;
  if (typeof value.usedSlots !== "number" || !Number.isFinite(value.usedSlots)) return false;
  if (!isStringArray(value.equipped)) return false;
  return true;
}

function isPartyMember(value: unknown): value is SaveData["party"][number] {
  if (!isRecord(value)) return false;
  if (typeof value.speciesId !== "string") return false;
  if (value.nickname !== undefined && typeof value.nickname !== "string") return false;
  if (typeof value.level !== "number" || !Number.isFinite(value.level)) return false;
  if (typeof value.xp !== "number" || !Number.isFinite(value.xp)) return false;
  if (typeof value.hpCurrent !== "number" || !Number.isFinite(value.hpCurrent)) return false;
  if (typeof value.hpMax !== "number" || !Number.isFinite(value.hpMax)) return false;
  if (!isStringArray(value.moves)) return false;
  if (!isNumberRecord(value.pp)) return false;
  // Optional `loadout` — when present must conform to PluginLoadout shape.
  if (value.loadout !== undefined && !isPluginLoadout(value.loadout)) return false;
  return true;
}

function isPcBoxEntry(value: unknown): value is SaveData["pcBox"][number] {
  if (!isRecord(value)) return false;
  return (
    typeof value.speciesId === "string" &&
    typeof value.level === "number" &&
    Number.isFinite(value.level) &&
    typeof value.xp === "number" &&
    Number.isFinite(value.xp)
  );
}

function isProgress(value: unknown): value is SaveData["progress"] {
  if (!isRecord(value)) return false;
  return (
    isStringArray(value.defeatedTrainers) &&
    isBooleanRecord(value.flags) &&
    isStringArray(value.seenSpecies) &&
    isStringArray(value.caughtSpecies)
  );
}

function isSaveData(value: unknown): value is SaveData {
  if (!isRecord(value)) return false;
  if (typeof value.version !== "number" || !Number.isFinite(value.version)) return false;
  if (typeof value.savedAt !== "number" || !Number.isFinite(value.savedAt)) return false;
  if (typeof value.playTimeMs !== "number" || !Number.isFinite(value.playTimeMs)) return false;
  if (!isPlayer(value.player)) return false;
  if (!Array.isArray(value.party) || !value.party.every(isPartyMember)) return false;
  if (!Array.isArray(value.pcBox) || !value.pcBox.every(isPcBoxEntry)) return false;
  if (!isNumberRecord(value.inventory)) return false;
  // v2+: owned-plugin inventory. Optional in the type to keep legacy
  // SaveData literals valid; when present must be a Record<string, number>.
  if (value.pluginInventory !== undefined && !isNumberRecord(value.pluginInventory)) return false;
  if (!isProgress(value.progress)) return false;
  return true;
}

// ---------- migrations ----------

/**
 * Migration step: takes data shaped at version N and returns version N+1.
 * Add a new entry whenever SAVE_VERSION is bumped. v1 has no prior version
 * so the table is currently empty.
 */
type MigrationStep = (raw: Record<string, unknown>) => Record<string, unknown> | null;

const MIGRATIONS: Record<number, MigrationStep> = {
  // v1 → v2: introduce per-party `loadout`, top-level `pluginInventory`
  // (owned plugins, keyed by pluginId → count), and a `player.coins`
  // soft-currency balance. All new fields default to safe empty values;
  // existing v1 fields are preserved.
  1: (raw) => {
    const partyIn = Array.isArray(raw.party) ? raw.party : [];
    const playerIn = isRecord(raw.player) ? raw.player : {};
    const migratedParty = partyIn.map((m) => {
      const member = isRecord(m) ? m : {};
      return { ...member, loadout: undefined };
    });
    return {
      ...raw,
      version: 2,
      party: migratedParty,
      pluginInventory: {},
      player: { ...playerIn, coins: 0 },
    };
  },
};

export function migrateIfNeeded(raw: unknown): SaveData | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.version !== "number" || !Number.isFinite(raw.version)) return null;

  let current: Record<string, unknown> = raw;
  let version: number = raw.version;

  // Refuse to load future saves we can't downgrade.
  if (version > SAVE_VERSION) return null;

  while (version < SAVE_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) return null;
    const next = step(current);
    if (next === null) return null;
    current = next;
    const nextVersion = current.version;
    if (typeof nextVersion !== "number" || nextVersion <= version) return null;
    version = nextVersion;
  }

  return isSaveData(current) ? current : null;
}

// ---------- public API ----------

export function saveGame(slot: SaveSlotId, data: SaveData): boolean {
  if (!hasStorage()) return false;
  if (!isValidSlot(slot)) return false;
  if (!isSaveData(data)) return false;
  try {
    const payload = JSON.stringify(data);
    window.localStorage.setItem(keyFor(slot), payload);
    return true;
  } catch {
    return false;
  }
}

export function loadGame(slot: SaveSlotId): SaveData | null {
  if (!hasStorage()) return null;
  if (!isValidSlot(slot)) return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(keyFor(slot));
  } catch {
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isRecord(parsed)) return null;

  if (typeof parsed.version === "number" && parsed.version < SAVE_VERSION) {
    return migrateIfNeeded(parsed);
  }

  return isSaveData(parsed) ? parsed : null;
}

export type SaveSummary = {
  slot: SaveSlotId;
  savedAt: number;
  playTimeMs: number;
  partyLevels: number[];
};

export function listSaves(): Array<SaveSummary | null> {
  if (!hasStorage()) {
    return VALID_SLOTS.map(() => null);
  }
  return VALID_SLOTS.map((slot) => {
    const data = loadGame(slot);
    if (!data) return null;
    return {
      slot,
      savedAt: data.savedAt,
      playTimeMs: data.playTimeMs,
      partyLevels: data.party.map((p) => p.level),
    };
  });
}

export function deleteSave(slot: SaveSlotId): void {
  if (!hasStorage()) return;
  if (!isValidSlot(slot)) return;
  try {
    window.localStorage.removeItem(keyFor(slot));
  } catch {
    // swallow — nothing the caller can do
  }
}
